import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api.js';
import { useScopeKey } from './useScopeKey.js';

function buildParams(filters = {}) {
  const p = new URLSearchParams();
  if (filters.status) p.set('status', filters.status);
  if (filters.from) p.set('from', filters.from);
  if (filters.to) p.set('to', filters.to);
  const str = p.toString();
  return str ? `?${str}` : '';
}

export function useClientInvoices(filters = {}) {
  const scopeKey = useScopeKey();
  return useQuery({
    queryKey: ['client-invoices', scopeKey, filters],
    queryFn: () => api.get(`/client-invoices${buildParams(filters)}`),
    staleTime: 30_000,
    enabled: scopeKey !== 'loading',
  });
}

// Une facture ouverte/payée est projetée dans "À payer" (source client_invoice)
// et dans le carve-out du cash-flow — toute mutation doit invalider les trois
// caches pour éviter un affichage périmé sur ces deux vues.
function invalidateAll(qc) {
  qc.invalidateQueries({ queryKey: ['client-invoices'] });
  qc.invalidateQueries({ queryKey: ['upcoming-events'] });
  qc.invalidateQueries({ queryKey: ['cash-flow'] });
}

export function useCreateClientInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api.post('/client-invoices', body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateClientInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.patch(`/client-invoices/${id}`, body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteClientInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.delete(`/client-invoices/${id}`),
    onSuccess: () => invalidateAll(qc),
  });
}

/**
 * body : { paid_at?, transaction_id? } — voir POST /client-invoices/:id/mark-paid.
 * Appel : mutateAsync({ id, paid_at, transaction_id? })
 */
export function useMarkClientInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api.post(`/client-invoices/${id}/mark-paid`, body),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUnmarkClientInvoicePaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => api.post(`/client-invoices/${id}/unmark-paid`),
    onSuccess: () => invalidateAll(qc),
  });
}
